import { ArrowRight } from "lucide-react";

import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { PERCENTAGE_INPUT_PATTERN } from "@/shared/utils/decimal";
import type { PaymentTaxBase } from "@/shared/utils/paymentTaxes";

import { defaultExampleAmount } from "../../constants/paymentFees.constants";
import { EXAMPLE_COMMISSION } from "../../constants/paymentTaxes.constants";
import { taxAmount } from "../../utils/taxMath";

export interface TaxExampleProps {
  base: PaymentTaxBase;
  name: string;
  rate: string;
  currency: string;
  className?: string;
}

/** The rate worked on a real figure — "Comisión 1.000 → IVA 190" — so the base is never misread. */
export function TaxExample({ base, name, rate, currency, className }: TaxExampleProps) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  if (!currency || !PERCENTAGE_INPUT_PATTERN.test(rate.trim())) return null;

  const baseAmount = base === "COMMISSION" ? EXAMPLE_COMMISSION : defaultExampleAmount(currency);
  const amount = taxAmount(baseAmount, rate.trim(), currency);
  const money = (value: string) => `${formatCurrencyAmount(value, currency, activeLanguage)} ${currency}`;

  return (
    <span
      className={cn(
        "inline-flex flex-wrap items-center gap-1.5 rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs tabular-nums text-muted-foreground",
        className,
      )}
    >
      <span>
        {t(base === "COMMISSION" ? "example_commission" : "example_gross", { amount: money(baseAmount) })}
      </span>
      <ArrowRight className="size-3 shrink-0" aria-hidden="true" />
      <span className="font-semibold text-foreground">
        {name || t("example_tax_fallback")} {money(amount)}
      </span>
    </span>
  );
}
